import React from "react";
import { useNavigate } from "react-router-dom";
import { LogoBrand } from "@/components/Logo";
import { Mail, Instagram, ArrowUp } from "lucide-react";

const footerLinks = {
  about: [
    { label: "About Us", path: "/about" },
    { label: "Contact Us", path: "/contact" },
  ],
  legal: [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Terms of Service", path: "/terms" },
    { label: "Refund Policy", path: "/refund" },
  ],
  features: [
    { label: "AI Doubt Solver", path: "/doubt" },
    { label: "Practice Tests", path: "/test" },
    { label: "Exam Countdown", path: "/exam-countdown" },
    { label: "Homework Solver", path: "/homework" },
  ],
};

const socials = [
  { icon: Instagram, href: "https://www.instagram.com/aistuddybuddy?igsh=MWpmczNjemd3NnJtNA%3D%3D&utm_source=qr", label: "Instagram" },
];

export default function Footer() {
  const navigate = useNavigate();

  return (
    <footer className="border-t border-slate-200 pt-12 pb-6 bg-white" data-testid="app-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Top Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-1">
            <LogoBrand size="sm" />
            <p className="text-slate-500 text-xs mt-3 leading-relaxed max-w-[260px]">
              India&apos;s smartest AI tutor for Class 5-12, CBSE &amp; ICSE boards. We help students understand every topic clearly with AI-powered explanations, practice tests, doubt solving, and exam preparation — so they can focus on learning and score better in exams.
            </p>
            <div className="flex gap-3 mt-4">
              {socials.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center hover:bg-indigo-100 hover:border-indigo-300 transition-all">
                  <s.icon className="w-3.5 h-3.5 text-slate-400 hover:text-indigo-600" />
                </a>
              ))}
            </div>
          </div>

          {/* About */}
          <div>
            <h4 className="font-heading font-bold text-slate-800 text-sm mb-3">About</h4>
            <ul className="space-y-2">
              {footerLinks.about.map(link => (
                <li key={link.path}>
                  <button onClick={() => navigate(link.path)} className="text-xs text-slate-500 hover:text-indigo-600 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
              <li>
                <a href="mailto:aistuddybuddy3@gmail.com" className="text-xs text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1">
                  <Mail className="w-3 h-3" /> aistuddybuddy3@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-bold text-slate-800 text-sm mb-3">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map(link => (
                <li key={link.path}>
                  <button onClick={() => navigate(link.path)} className="text-xs text-slate-500 hover:text-indigo-600 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h4 className="font-heading font-bold text-slate-800 text-sm mb-3">Features</h4>
            <ul className="space-y-2">
              {footerLinks.features.map(link => (
                <li key={link.path}>
                  <button onClick={() => navigate(link.path)} className="text-xs text-slate-500 hover:text-indigo-600 transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-200 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">
            &copy; {new Date().getFullYear()} AI Studdy Buddy. All rights reserved. | Grades 5-12 | CBSE & ICSE
          </p>
          <p className="text-[10px] text-slate-300">
            AI-generated content may occasionally contain errors. Always verify with your textbook.
          </p>
        </div>
      </div>
    </footer>
  );
}
